import React from 'react';
import { observer } from 'mobx-react';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { Modal } from 'ui/base/modal';
import { InputKeyEvent } from 'ui/base/types';
import { BackButton } from 'ui/base/button/back_button';
import { ScrollableArea } from 'ui/base/page/scrollable_area';
import { TextInput, URLInput } from 'ui/public/input';
import { CustomInputStore } from './custom_input_store';
import baseStyles from './custom_input_modal.module.css';
import { ErrorMessage } from 'ui/public/error_message/error_message';

type CustomPolicyStyles = {
  modalContainer: string;
  column: string;
  headerContainer: string;
  contentContainer: string;
  instructions: string;
  errorContainer: string;
  centered: string;
  accept: string;
};

const themedStyles: ThemedStyles<CustomPolicyStyles> = {
  [Theme.DAYLIGHT]: {
    modalContainer: baseStyles.modalContainer,
    column: baseStyles.column,
    headerContainer: baseStyles.headerContainer,
    contentContainer: baseStyles.contentContainer,
    instructions: baseStyles.instructions,
    errorContainer: baseStyles.errorContainer,
    centered: baseStyles.centered,
    accept: baseStyles.accept,
  },
  [Theme.MIDNIGHT]: {
    modalContainer: baseStyles.modalContainer,
    column: baseStyles.column,
    headerContainer: baseStyles.headerContainer,
    contentContainer: baseStyles.contentContainer,
    instructions: baseStyles.instructions,
    errorContainer: baseStyles.errorContainer,
    centered: baseStyles.centered,
    accept: baseStyles.accept,
  },
};

export enum InputModalType {
  TEXT = 'text',
  URL_STRICT = 'url-strict',
  URL_NONSTRICT = 'url-nonstrict',
}

type CustomInputModalProps = {
  store: CustomInputStore;
  instructions: string;
  saveMessage: string;
  onCommit(): void;
  invalidMessage: string;
  type?: InputModalType;
};

export const CustomInputModal = observer(({
  store,
  instructions,
  saveMessage,
  onCommit,
  invalidMessage,
  type,
}: CustomInputModalProps) => {
  const styles = useStyles(themedStyles);

  /*
  The implementation here is a hack to get around the HTML restriction
  that forms cannot be nested. This 'modal' may or may not be contained
  by some enclosing form (though I think, in all usages, there's an
  enclosing form).

  Pressing 'Enter' on an input contained in a form
  triggers the 'submit' of that form, which we do not want here; we
  only want to call onCommit (the action of this 'modal'). So we
  capture the Enter event.

  The submit button is also a div so that it doesn't trigger a submit.

  I think a better implementation would be to not place modal components
  inside the components that trigger opening them. A special separate
  handling of modals might be better.
  */
  const handleKeyPress = React.useCallback((event: InputKeyEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      onCommit();
    }
  }, [onCommit]);
  return (
    <Modal store={store.modal}>
      <div className={styles.modalContainer}>
        <ScrollableArea>
          <div className={styles.column}>
            <div className={styles.headerContainer}>
              <BackButton onClick={store.modal.hideModal}/>
            </div>
            <div className={styles.contentContainer}>
              <div>
                <div className={styles.instructions}>
                  {instructions}
                </div>
                {type == null || type === InputModalType.TEXT ?
                  <TextInput store={store.custom} onKeyPress={handleKeyPress}/> :
                  type === InputModalType.URL_NONSTRICT ?
                    <URLInput store={store.custom} onKeyPress={handleKeyPress} type='text'/> :
                    type === InputModalType.URL_STRICT ?
                      <URLInput store={store.custom} onKeyPress={handleKeyPress}/> :
                      <></>
                }
                {!store.isValid.value &&
                  <div className={styles.errorContainer}>
                    <ErrorMessage message={invalidMessage}/>
                  </div>
                }
                <div className={styles.centered} onClick={onCommit}>
                  <div className={styles.accept}>
                    {saveMessage}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ScrollableArea>
      </div>
    </Modal>
  );
});
